import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Loader2, Lock, CheckCircle2, FileText } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InvoicePay() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const [inv, setInv] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [paypalOn, setPaypalOn] = useState(false);

  useEffect(() => {
    axios.get(`${API}/paypal/status`).then((r) => setPaypalOn(!!r.data.configured)).catch(() => {});
    if (searchParams.get("paypal") === "failed") {
      setError("PayPal payment didn't complete — you can try again or pay by card.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/invoice/${token}`);
        setInv(r.data);
        // returning from Stripe — trigger fulfillment (webhook fallback) then poll
        const sid = searchParams.get("session_id");
        if (searchParams.get("paid") === "1" && r.data.status !== "paid") {
          setConfirming(true);
          if (sid) {
            try { await axios.get(`${API}/payments/status/${sid}`); } catch {}
            try {
              const again0 = await axios.get(`${API}/invoice/${token}`);
              setInv(again0.data);
              if (again0.data.status === "paid") return;
            } catch {}
          }
          let tries = 0;
          const iv = setInterval(async () => {
            tries += 1;
            try {
              const again = await axios.get(`${API}/invoice/${token}`);
              if (again.data.status === "paid") {
                setInv(again.data);
                setConfirming(false);
                clearInterval(iv);
              }
            } catch {}
            if (tries >= 20) { setConfirming(false); clearInterval(iv); }
          }, 2000);
        }
      } catch {
        setError("This invoice link is invalid or has expired.");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const pay = async () => {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const res = await axios.post(`${API}/invoice/${token}/checkout`, {
        origin_url: window.location.origin,
      });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not start card payment — please call us.");
      setBusy(false);
    }
  };

  const payWithPaypal = async () => {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const res = await axios.post(`${API}/invoice/${token}/paypal`, {
        origin_url: window.location.origin,
      });
      window.location.href = res.data.approve_url;
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not start PayPal — card payment is still available.");
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F7F3] py-10 px-4" data-testid="invoice-pay-page">
      <div className="max-w-xl mx-auto">
        <div className="text-center mb-6">
          <div className="font-serif italic text-3xl text-[#3F5043]">Soarlife</div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#899480]">Consulting LLC</div>
        </div>

        {error && !inv && (
          <div className="bg-white border border-[#875F45]/30 rounded-3xl p-8 text-center text-sm text-[#875F45]" data-testid="pay-error">
            {error}
          </div>
        )}

        {!inv && !error && (
          <div className="text-center py-16"><Loader2 size={22} className="animate-spin inline text-[#899480]" /></div>
        )}

        {inv && (
          <div className="bg-white border border-[#BCB3A6]/40 rounded-3xl p-6 sm:p-8" data-testid="invoice-card">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-[#899480] font-semibold mb-1">
              <FileText size={12} /> Invoice
            </div>
            <div className="font-serif text-2xl text-[#3F5043] mb-1" data-testid="invoice-number">{inv.invoice_number}</div>
            <div className="text-xs text-[#899480] mb-5">
              Billed to {inv.client_name}
              {inv.invoice_date ? ` · Issued ${inv.invoice_date}` : ""}
              {inv.due_date ? ` · Due ${inv.due_date}` : ""}
            </div>

            <div className="border-t border-[#EDEDEE] pt-4 mb-4" data-testid="invoice-items">
              {inv.items.map((it, i) => (
                <div key={i} className="flex justify-between text-sm py-1.5">
                  <span className="text-[#3F5043]">{it.description}{it.quantity > 1 ? ` × ${it.quantity}` : ""}</span>
                  <span className="text-[#3F5043]/80">${(it.quantity * it.unit_price).toFixed(2)}</span>
                </div>
              ))}
              {inv.tax > 0 && (
                <div className="flex justify-between text-sm py-1.5 border-t border-[#EDEDEE] mt-2 pt-2">
                  <span className="text-[#899480]">Tax</span>
                  <span className="text-[#899480]">${inv.tax.toFixed(2)}</span>
                </div>
              )}
            </div>

            <div className="flex justify-between items-center border-t border-[#3F5043]/20 pt-3 mb-5">
              <span className="text-xs uppercase tracking-[0.2em] text-[#899480] font-semibold">Total due</span>
              <span className="font-serif text-2xl text-[#3F5043]" data-testid="invoice-total">${inv.total.toFixed(2)}</span>
            </div>

            {inv.notes && <p className="text-xs text-[#899480] mb-5 leading-relaxed">{inv.notes}</p>}

            {inv.status === "paid" ? (
              <div className="bg-[#899480]/10 border border-[#899480]/30 rounded-2xl p-4 text-center" data-testid="pay-status-paid">
                <CheckCircle2 size={20} className="inline text-[#3F5043] mb-1" />
                <div className="text-sm font-medium text-[#3F5043]">Paid — thank you.</div>
                <div className="text-xs text-[#899480] mt-1">A receipt was sent to your email. Questions? (407) 374-9834</div>
              </div>
            ) : inv.status === "void" ? (
              <div className="bg-[#EDEDEE] rounded-2xl p-4 text-center text-sm text-[#899480]" data-testid="pay-status-void">
                This invoice is void. Contact Soarlife if you believe this is an error.
              </div>
            ) : confirming ? (
              <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/40 rounded-2xl p-4 text-center" data-testid="pay-confirming">
                <Loader2 size={18} className="animate-spin inline text-[#875F45] mb-1" />
                <div className="text-sm text-[#3F5043]">Confirming your payment…</div>
              </div>
            ) : (
              <>
                <button onClick={pay} disabled={busy} data-testid="pay-button"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 bg-[#3F5043] text-[#F8F7F3] text-sm uppercase tracking-[0.15em] font-semibold hover:bg-[#875F45] transition-colors disabled:opacity-60">
                  {busy ? <Loader2 size={15} className="animate-spin" /> : <Lock size={14} />}
                  Pay ${inv.total.toFixed(2)} securely by card
                </button>
                {paypalOn && (
                  <button onClick={payWithPaypal} disabled={busy} data-testid="paypal-button"
                    className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 border-2 border-[#D4AF37] text-[#875F45] text-sm uppercase tracking-[0.15em] font-semibold hover:bg-[#D4AF37]/10 transition-colors disabled:opacity-60">
                    {busy ? <Loader2 size={15} className="animate-spin" /> : null}
                    Pay with PayPal
                  </button>
                )}
                {error && <div className="mt-3 text-center text-xs text-[#875F45]" data-testid="pay-error-inline">{error}</div>}
              </>
            )}
          </div>
        )}

        <p className="text-center text-[11px] text-[#899480] mt-6">
          Payments processed securely by Stripe · Soarlife Consulting · (407) 374-9834 · contact@soarlife.consulting
        </p>
      </div>
    </div>
  );
}
